import { useCallback, useMemo } from 'react'

import { Icon } from '@circlefin/components'
import { createColumnHelper } from '@circlefin/components/lib/AdvancedTable'
import { CopyToClipboard } from '@circlefin/components/lib/CopyToClipboard'
import { usePermission } from '@circlefin/permissions/react'
import { useTravelRuleRequired } from '@features/users/hooks/travel-rule'
import { ADDRESS_BOOK_MUTATION } from '@services/permissions'
import { getBlockchain } from '@services/static-data/blockchains'
import { DELAYED_WITHDRAWALS_SETTINGS_POLL_INTERVAL } from '@shared/apollo/pollInterval'
import { Center } from '@shared/components/layout'
import { useDelayedWithdrawalsSettingsQuery } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { AddressStatus } from '../../components'
import { AddressBookActionsMenu } from '../AddressBookActionsMenu/AddressBookActionsMenu'

import type { BlockchainRecipientAddressWithMetadata } from '@shared/graphql'

const columnHelper =
  createColumnHelper<BlockchainRecipientAddressWithMetadata>()

interface UseAddressBookTableColumnsProps {
  /**
   * Controls visibility of the action cell.
   */
  showActionCell?: boolean
}

export const useAddressBookTableColumns = ({
  showActionCell = true,
}: UseAddressBookTableColumnsProps = {}) => {
  const { t } = useTranslation('address-book/containers')
  const [, { isAuthorized }] = usePermission(ADDRESS_BOOK_MUTATION)

  const { data } = useDelayedWithdrawalsSettingsQuery({
    pollInterval: DELAYED_WITHDRAWALS_SETTINGS_POLL_INTERVAL,
    fetchPolicy: 'cache-and-network',
  })

  const { travelRuleRequired } = useTravelRuleRequired()

  const showActionsMenu = useMemo(
    () => isAuthorized && !travelRuleRequired,
    [isAuthorized, travelRuleRequired],
  )

  const showStatusColumn = useMemo(
    () => data?.delayedWithdrawalsSettings.enabled || travelRuleRequired,
    [data?.delayedWithdrawalsSettings.enabled, travelRuleRequired],
  )

  // To prevent page navigation when clicking on the menu icon, we need to stop the event propagation
  const onClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => e.stopPropagation(),
    [],
  )

  return useMemo(() => {
    /**
     * Icon (Nickname) Column.
     */
    const nickname = columnHelper.accessor('description', {
      header: t<string>`address-book-table.headers.name.customer`,
      meta: {
        className: 'w-32',
      },
      cell: ({ getValue }) => (
        <div className="truncate type-body-sm-bold">{getValue()}</div>
      ),
    })

    /**
     * Chain (Blockchain) Column.
     */
    const chain = columnHelper.accessor('chain', {
      header: t<string>`address-book-table.headers.blockchain`,
      meta: {
        className: 'w-16',
      },
      cell: ({ getValue }) => getBlockchain(getValue())?.name,
    })

    /**
     * Status Column.
     */
    const status = columnHelper.accessor('status', {
      header: t<string>`address-book-table.headers.status`,
      meta: {
        className: 'w-20',
      },
      cell: ({ getValue }) => <AddressStatus status={getValue()} />,
    })

    /**
     * Currency Column.
     */
    const currency = columnHelper.accessor('currency', {
      header: t<string>`common:currency`,
      meta: {
        className: 'w-16',
      },
      cell: ({ getValue }) => getValue(),
    })

    /**
     * Address (Wallet address) Column.
     */
    const address = columnHelper.accessor('address', {
      header: t<string>`address-book-table.headers.walletAddress`,
      cell: ({ getValue }) => (
        <CopyToClipboard value={getValue()} variant="inline" />
      ),
    })

    /**
     * Action Column.
     */
    const action = columnHelper.display({
      id: 'action',
      meta: {
        className: 'w-5 p-0',
      },
      cell: ({ row: { original } }) =>
        showActionsMenu && (
          <Center onClick={onClick} variant="vertical">
            <AddressBookActionsMenu recipientAddress={original} />
          </Center>
        ),
    })

    /**
     * Arrow Icon Column.
     */
    const arrowIcon = columnHelper.display({
      id: 'arrowIcon',
      meta: {
        className: 'w-5 p-0 pr-8',
      },
      cell: () => (
        <Icon
          className="mx-auto text-center text-black-500"
          name="ChevronRightSolid"
          size={20}
        />
      ),
    })

    return [
      nickname,
      address,
      chain,
      currency,
      ...(showStatusColumn ? [status] : []),
      ...(showActionCell ? [action, arrowIcon] : []),
    ]
  }, [t, showStatusColumn, showActionCell, showActionsMenu, onClick])
}
